/**
 * Budgets: a spend limit per period on the organisation, a business area, a project, a team (or a
 * specialist, which is a team of one) or
 * a person, each shown with what is spent, what remains and where it stands. Past the limit
 * the budget's action applies: warn, pause new work, or block running work. Cost round 2: the
 * platform works budgets out on its own and tells people at 50, 80 and 100 per cent and when the
 * run-rate says the limit will be reached; lifts are per project; administrators choose the time
 * zone periods turn over in and whether workbenches stop when they cannot reach the platform.
 */

import { BUDGET_STATE_TEXT, type Budget, type BudgetStanding, type Verb } from '@aioe/shared';
import { dateTime } from '@aioe/shared/wording/dateTime';
import { CreditCard } from '@gravity-ui/icons';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { problemText, routes } from '../api/client.ts';
import { SubjectName, SubjectPicker } from '../components/SubjectPicker.tsx';
import { Button, ChoiceSelect, ConfirmDialog, Empty, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, PageColumn, PageHeader, Stat, StatusMark, Toggle } from '../components/ui.tsx';
import { FormField, FormSheet } from '../components/ui/FormSheet.tsx';
import { RowMenu } from '../components/ui/RowMenu.tsx';
import { VIEW_TONES } from '../components/ui/Art.tsx';
import { Explainer } from '../components/ui/Layout.tsx';
import { timeAgo, useLoad } from '../hooks.ts';
import { useNames } from '../names.ts';
import { ProjectChips, ProjectPicker } from '../components/ProjectPicker.tsx';
import { useMoney } from '../money.tsx';
import { holds, scopesHolding, usePermissions } from '../permissions.ts';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';

const PERIOD_TEXT = { month: 'a month', week: 'a week', day: 'a day' } as const;
const ACTION_TEXT = { warn: 'Warn only', throttle: 'Pause new work', block: 'Block running work' } as const;
const ROLE_TEXT = { owner: "The project's owners", 'portfolio-manager': 'Portfolio managers', admin: 'Administrators' } as const;
const STATE_MARK: Record<BudgetStanding['state'], 'connected' | 'medium' | 'high' | 'error'> = { ok: 'connected', warning: 'medium', throttled: 'high', blocked: 'error' };

type Named = { id: string; name: string };
type ProjectRef = Named & { businessAreaId?: string };
type FormTarget = { editing?: BudgetStanding; scope?: Budget['scope'] };

export function BudgetsPage() {
  const tone = VIEW_TONES.budgets!;
  // What the person may do to a budget, from their verbs (docs/rbac.md step 5). With roles off the page is as it was: the
  // API refuses what an account may not. With roles on, an organisation-wide verb covers every budget; a team's
  // budgets are changed by someone assigned Team Administrator of that team (its owner views them), and the
  // organisation's, an area's, a project's and a person's stay with the organisation's roles. The API decides each call.
  const perms = usePermissions();
  const p = perms.data;
  const roles = p?.mode === 'roles';
  const waiting = perms.loading && p === undefined;
  const teamIds = (verb: Verb) => new Set(scopesHolding(p, 'economics.budgets', verb, { type: 'team', assigned: true }).map((s) => s.id));
  const may = (verb: Verb, scope: Budget['scope']): boolean => !waiting && (!roles || holds(p, 'economics.budgets', verb) || (scope.type === 'team' && teamIds(verb).has(scope.id)));
  const orgMay = (verb: Verb) => !waiting && (!roles || holds(p, 'economics.budgets', verb));
  const mayCreateTeams = roles ? [...teamIds('create')] : [];
  const mayNew = orgMay('create') || mayCreateTeams.length > 0;
  const viewOnly = roles && !waiting && !orgMay('update') && teamIds('update').size === 0;
  const budgets = useLoad(() => routes.budgets(), [], 60_000);
  const inherited = useLoad(() => routes.inheritedBudgets().catch(() => [] as BudgetStanding[]), [], 60_000);
  const settings = useLoad(() => routes.budgetSettings().catch(() => undefined), []);
  const timeZone = settings.data?.timeZone ?? 'UTC';
  const [checking, setChecking] = useState(false);
  /** The form is a sheet: open for a new budget (`scope` may preset one), for a change (`editing`), or closed. */
  const [target, setTarget] = useState<FormTarget>();
  const [removing, setRemoving] = useState<BudgetStanding>();
  const [lifting, setLifting] = useState<BudgetStanding>();
  const reload = () => {
    budgets.reload();
    inherited.reload();
  };
  const checkNow = async () => {
    setChecking(true);
    try {
      await routes.evaluateBudgets();
      reload();
    } finally {
      setChecking(false);
    }
  };
  const areas = useLoad(() => routes.areas().catch(() => []), []);
  // Teams and specialists: a specialist is a team of one, so a team budget on it holds its work (token economics, 0.90.0).
  const teams = useLoad(() => Promise.all([routes.teams().catch(() => []), routes.teams('specialist').catch(() => [])]).then(([t, s]) => [...t, ...s.filter((x) => !t.some((y) => y.id === x.id)).map((x) => ({ ...x, name: `${x.name} (specialist)` }))]), []);
  const rows = budgets.data ?? [];
  const shares = inherited.data ?? [];
  const everything = [...rows, ...shares];
  const over = everything.filter((r) => r.state === 'throttled' || r.state === 'blocked').length;
  const near = everything.filter((r) => r.state === 'warning').length;
  const areaList: Named[] = areas.data ?? [];
  // Names and areas for the projects that hold a budget (or share one), asked for by id; the form searches for the rest.
  const budgeted = [...rows, ...shares].flatMap((s) => (s.budget.scope.type === 'project' ? [s.budget.scope.id] : []));
  const named = useNames(budgeted);
  const projectList: ProjectRef[] = [...named.projects].map(([id, name]) => ({ id, name, ...(named.projectAreas.get(id) ? { businessAreaId: named.projectAreas.get(id)! } : {}) }));
  const teamList: Named[] = teams.data ?? [];
  const names = { area: new Map(areaList.map((a) => [a.id, a.name])), project: new Map(projectList.map((p) => [p.id, p.name])), team: new Map(teamList.map((t) => [t.id, t.name])) };
  const remove = async () => {
    if (!removing) return;
    await routes.deleteBudget(removing.budget.id);
    if (target?.editing?.budget.id === removing.budget.id) setTarget(undefined);
    setRemoving(undefined);
    reload();
  };
  const newButton = mayNew ? (
    <Button size="sm" variant="primary" onPress={() => setTarget({})}>
      New budget
    </Button>
  ) : null;
  return (
    <PageColumn width="data">
      <PageHeader
        tone={tone}
        icon={<CreditCard />}
        title="Budgets"
        subtitle={roles && !orgMay('read') ? 'The spend limits on the teams you look after, and where each one stands. Money budgets count metered spend only; token limits count every call.' : 'Spend limits per period on the organisation, its business areas and their projects, teams and people, and where each one stands. Money budgets count metered spend only; token limits count every call.'}
        actions={
          <>
            {!roles || orgMay('act') ? (
              <Button size="sm" variant="secondary" isPending={checking} onPress={() => void checkNow()}>
                Check now
              </Button>
            ) : null}
            {newButton}
          </>
        }
      />
      <Explainer id="budgets" tone={tone} icon={<CreditCard />} title="How budgets work" summary="A spend limit per period; people are told as spend nears it, and its action applies past it">
        <p>A budget is a limit on metered spend (and, if you set one, on tokens) per day, week or month, on the organisation, a business area, a project, a team or specialist, or a person. The organisation's budgets hold its areas', and an area's hold its projects'.</p>
        <p>The people concerned are told at 50, 80 and 100 per cent, and when the last seven days' pace says the limit will be reached before the period ends. Past the limit the budget warns, pauses new work, or blocks running work, until someone with the override role lifts it for a while.</p>
        <p>
          To see what the spend went on, by agent, session, repository and source, open <Link to="/costs" className="text-accent hover:underline">Costs</Link>: its overview shows each budget against the same period.
        </p>
      </Explainer>
      <PartBoundary key="budgets" name="Budgets" tone={tone}>
        <ErrorNote error={budgets.error} />
        {viewOnly && rows.length > 0 ? <ListBlock className="text-sm text-muted">You can see these budgets. Setting, changing or lifting one is for the people assigned as the team’s administrators; a team’s owner views it.</ListBlock> : null}
        <div className="art-stats">
          <Stat tone={tone} icon={<CreditCard />} label="Budgets" value={rows.length} detail={rows.length ? `${rows.filter((r) => r.state === 'ok').length} within budget${shares.length ? `, ${shares.length} inherited` : ''}` : 'None set yet'} />
          <Stat tone={tone} icon={<CreditCard />} label="Near the limit" value={near} detail={`${everything.filter((r) => r.forecast?.exceeds && r.percent < 100).length} on track to pass it`} />
          <Stat tone={tone} icon={<CreditCard />} label="Paused or blocked" value={over} detail={over ? 'Waiting on a lift or the next period' : 'Nothing held up'} needsYou={over > 0} />
        </div>
        {budgets.data && rows.length === 0 && shares.length === 0 ? (
          <ListGroup
            title="All budgets"
            empty={<Empty tone={tone} icon={<CreditCard />} title="No budgets yet" body="Set one on the organisation, a business area, a project, a team or a person. Its people are told as spend nears and passes the line." action={newButton} />}
          />
        ) : (
          <BudgetTree rows={rows} shares={shares} projects={projectList} names={names} timeZone={timeZone} onEdit={(s) => setTarget({ editing: s })} onRemove={setRemoving} onLift={setLifting} onOwn={(projectId) => setTarget({ scope: { type: 'project', id: projectId } })} may={may} />
        )}
        {!budgets.error && settings.data && (!roles || holds(p, 'organisation.settings', 'update')) && (
          <BudgetSettings
            initial={{ timeZone, failClosed: settings.data.budgets?.failClosed ?? false }}
            onSaved={() => {
              settings.reload();
              reload();
            }}
          />
        )}
      </PartBoundary>
      {target ? <BudgetForm key={target.editing?.budget.id ?? (target.scope ? JSON.stringify(target.scope) : 'new')} target={target} onDone={() => (setTarget(undefined), reload())} onCancel={() => setTarget(undefined)} areas={areaList} projects={projectList} teams={teamList} names={names} onlyTeams={roles && !orgMay('create') ? scopesHolding(p, 'economics.budgets', 'create', { type: 'team', assigned: true }).map((t) => ({ id: t.id, name: names.team.get(t.id) ?? t.name ?? t.id })) : undefined} /> : null}
      {lifting ? <LiftEverywhere s={lifting} onDone={() => (setLifting(undefined), reload())} onCancel={() => setLifting(undefined)} /> : null}
      <ConfirmDialog isOpen={Boolean(removing)} title={`Remove ${removing?.budget.name ?? 'the budget'}?`} confirmLabel="Remove the budget" danger onConfirm={remove} onCancel={() => setRemoving(undefined)}>
        <p>Its limit stops applying at once and nobody is told about it again. Spend already recorded stays on the ledger.</p>
        {removing?.budget.childDefault && <p>The projects in its business area that inherit a share of it lose that share too.</p>}
      </ConfirmDialog>
    </PageColumn>
  );
}

type Names = { area: Map<string, string>; project: Map<string, string>; team: Map<string, string> };
type RowActions = { onEdit: (s: BudgetStanding) => void; onRemove: (s: BudgetStanding) => void; onLift: (s: BudgetStanding) => void; onOwn: (projectId: string) => void; may: (verb: Verb, scope: Budget['scope']) => boolean };

/**
 * The tree, as one group per level: the organisation; each business area with its own budgets first
 * and its projects' beneath; projects in no area; then teams and people apart.
 */
function BudgetTree({ rows, shares, projects, names, timeZone, ...actions }: { rows: BudgetStanding[]; shares: BudgetStanding[]; projects: ProjectRef[]; names: Names; timeZone: string } & RowActions) {
  const of = (t: Budget['scope']['type']) => rows.filter((r) => r.budget.scope.type === t);
  const scopeId = (s: BudgetStanding) => (s.budget.scope.type === 'tenant' ? '' : s.budget.scope.id);
  const projectArea = new Map(projects.map((p) => [p.id, p.businessAreaId ?? '']));
  const forProjects = [...of('project'), ...shares];
  const areaIds = [...new Set([...of('area').map(scopeId), ...forProjects.map((s) => projectArea.get(scopeId(s)) ?? '').filter(Boolean)])];
  const areaOrder = areaIds.sort((a, b) => (names.area.get(a) ?? a).localeCompare(names.area.get(b) ?? b));
  const loose = forProjects.filter((s) => !projectArea.get(scopeId(s)));
  // An inherited share names its area's budget by the area, unless the area gives shares from more than one budget.
  const giving = new Map<string, number>();
  for (const b of of('area')) if (b.budget.childDefault) giving.set(scopeId(b), (giving.get(scopeId(b)) ?? 0) + 1);
  const fromText = (s: BudgetStanding) => {
    if (!s.inherited) return undefined;
    const area = projectArea.get(scopeId(s));
    const areaName = area ? names.area.get(area) : undefined;
    return areaName && (giving.get(area!) ?? 0) <= 1 ? `Inherited: ${s.inherited.percent}% of ${areaName}'s budget` : `Inherited: ${s.inherited.percent}% of ${s.inherited.name}`;
  };
  const row = (s: BudgetStanding) => <BudgetRow key={s.budget.id} s={s} names={names} inheritedText={fromText(s)} timeZone={timeZone} {...actions} />;
  const byProject = (list: BudgetStanding[]) => list.sort((a, b) => (names.project.get(scopeId(a)) ?? '').localeCompare(names.project.get(scopeId(b)) ?? '') || Number(Boolean(a.inherited)) - Number(Boolean(b.inherited)));
  return (
    <>
      {of('tenant').length > 0 && (
        <ListGroup title="The organisation" count={of('tenant').length} description="Holds every business area's budgets, and through them their projects'.">
          {of('tenant').map((s) => row(s))}
        </ListGroup>
      )}
      {areaOrder.map((areaId) => {
        const own = of('area').filter((s) => scopeId(s) === areaId);
        const beneath = byProject(forProjects.filter((s) => projectArea.get(scopeId(s)) === areaId));
        return (
          <ListGroup key={areaId} title={names.area.get(areaId) ?? areaId} count={own.length + beneath.length} description={own.length ? 'The business area, then its projects.' : 'No budget on the business area itself; its projects below.'}>
            {own.map((s) => row(s))}
            {beneath.map((s) => row(s))}
          </ListGroup>
        );
      })}
      {loose.length > 0 && (
        <ListGroup title="Projects in no business area" count={loose.length}>
          {byProject(loose).map((s) => row(s))}
        </ListGroup>
      )}
      {of('team').length > 0 && (
        <ListGroup title="Teams and specialists" count={of('team').length}>
          {of('team').map((s) => row(s))}
        </ListGroup>
      )}
      {of('person').length > 0 && (
        <ListGroup title="People" count={of('person').length}>
          {of('person').map((s) => row(s))}
        </ListGroup>
      )}
    </>
  );
}

/**
 * One budget as it stands: its name, where it applies and the money in words; its state at the end
 * with one menu; the meter, the forecast and its rules under it. An inherited share says whose budget
 * it is a share of and is changed there.
 */
function BudgetRow({ s, names, inheritedText, timeZone, onEdit, onRemove, onLift, onOwn, may }: { s: BudgetStanding; names: Names; inheritedText?: string; timeZone: string } & RowActions) {
  const { format } = useMoney();
  const navigate = useNavigate();
  const scope = s.budget.scope;
  const projectId = scope.type === 'project' ? scope.id : undefined;
  const title = s.inherited ? (projectId ? (names.project.get(projectId) ?? projectId) : s.budget.name) : s.budget.name;
  const where =
    scope.type === 'tenant' ? 'The whole organisation' : scope.type === 'person' ? <SubjectName id={scope.id} /> : scope.type === 'area' ? 'The business area' : scope.type === 'team' ? `Team ${names.team.get(scope.id) ?? scope.id}` : names.project.get(scope.id) && names.project.get(scope.id) !== title ? names.project.get(scope.id) : 'The project';
  const held = s.state === 'throttled' || s.state === 'blocked';
  // Tokens against their limit, and what the money leaves out: one line under the meter.
  const extra = [s.budget.tokens ? `${s.spent.tokens.toLocaleString()} of ${s.budget.tokens.toLocaleString()} tokens` : '', notCounted(s.spent, format).replace(/^ · /, '')].filter(Boolean).join(' · ').replace(/^./, (c) => c.toUpperCase());
  return (
    <ListRow
      title={title}
      description={
        <>
          {s.inherited ? inheritedText : where}
          {' · '}
          <span className="tabular-nums">{format(s.spent.usd)}</span> of <span className="tabular-nums">{format(s.budget.amountUsd)}</span> {PERIOD_TEXT[s.budget.period]} (<span className="tabular-nums">{s.percent}%</span>), <span className="tabular-nums">{format(s.remaining.usd)}</span> left
        </>
      }
      status={<StatusMark state={STATE_MARK[s.state]} label={BUDGET_STATE_TEXT[s.state]} />}
      compactControl
      control={
        <RowMenu
          label={`More for ${title}`}
          items={
            s.inherited
              ? [!!projectId && may('create', scope) && { id: 'own', label: 'Give it its own budget' }, !!projectId && { id: 'project', label: 'Open the project' }]
              : [held && may('act', scope) && { id: 'lift', label: 'Lift for every project' }, may('update', scope) && { id: 'edit', label: 'Change' }, !!projectId && { id: 'project', label: 'Open the project' }, may('delete', scope) && { id: 'remove', label: 'Remove', danger: true }]
          }
          onAction={(id) => {
            if (id === 'own' && projectId) onOwn(projectId);
            else if (id === 'project' && projectId) navigate(`/projects/${encodeURIComponent(projectId)}`);
            else if (id === 'lift') onLift(s);
            else if (id === 'edit') onEdit(s);
            else if (id === 'remove') onRemove(s);
          }}
        />
      }
    >
      <div className="grid gap-1.5 text-xs text-muted">
        <Meter s={s} />
        {extra ? <p>{extra}</p> : null}
        {s.forecast && <p>{forecastWords(s, timeZone, format)}</p>}
        <p>
          {`${ACTION_TEXT[s.budget.action]} at 100%, warn at ${s.budget.warnAtPercent}%. People are told at 50, 80 and 100%`}
          {s.thresholds?.length ? ` (passed so far: ${s.thresholds.map((t) => `${t}%`).join(', ')})` : ''}.
          {s.budget.childDefault ? ` Each project in the area without a budget of its own inherits ${s.budget.childDefault.percent}% of it.` : ''}
          {s.inherited ? ' Change it on the business area’s budget, or give the project a budget of its own.' : ''}
        </p>
        {(s.lifts ?? []).map((l) => (
          <p key={l.projectId}>
            {l.projectId ? `Lifted for ${l.projectName ?? l.projectId}` : 'Lifted for every project'} by <SubjectName id={l.by} /> until {dateTime(l.until, { date: 'medium', timeZone })}: {l.reason}
          </p>
        ))}
      </div>
    </ListRow>
  );
}

/** Lifting a held budget for every project it covers, for a day, with a reason that is kept with the lift. */
function LiftEverywhere({ s, onDone, onCancel }: { s: BudgetStanding; onDone: () => void; onCancel: () => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const lift = async () => {
    setError(undefined);
    setBusy(true);
    try {
      await routes.liftBudgetEverywhere(s.budget.id, { reason: reason.trim(), hours: 24 });
      onDone();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormSheet
      width="md"
      title={`Lift ${s.budget.name}`}
      description="Work goes on in every project it covers for the next 24 hours. The reason is kept with the lift and shown on the budget."
      onClose={onCancel}
      footer={
        <>
          <Button size="sm" variant="tertiary" onPress={onCancel}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" isPending={busy} isDisabled={!reason.trim()} onPress={() => void lift()}>
            Lift for a day
          </Button>
        </>
      }
    >
      <FormField label="Why work should go on everywhere">
        <input className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Month-end close needs the warehouse jobs" autoFocus />
      </FormField>
      <ErrorNote error={error} />
    </FormSheet>
  );
}

type ScopeType = Budget['scope']['type'];
const SCOPE_TEXT: Record<ScopeType, string> = { tenant: 'The whole organisation', area: 'A business area', project: 'A project', team: 'A team or specialist', person: 'A person' };
const EVERY_TEXT: Record<Budget['period'], string> = { month: 'Month', week: 'Week', day: 'Day' };

/** The project a budget is on, chosen by searching for it (the list of projects is never loaded whole). */
function ProjectScope({ id, onPick }: { id: string; onPick: (id: string) => void }) {
  return (
    <div className="grid gap-2">
      {id ? <ProjectChips ids={[id]} onRemove={() => onPick('')} /> : null}
      {id ? null : <ProjectPicker onPick={(p) => onPick(p.id)} />}
    </div>
  );
}

/** A new budget, or a change to one, in a sheet: the scope is fixed once set; an area budget may give its projects a default share. */
function BudgetForm({ target, onDone, onCancel, areas, projects, teams: allTeams, names, onlyTeams }: { target: FormTarget; onDone: () => void; onCancel: () => void; areas: Named[]; projects: Named[]; teams: Named[]; names: Names; onlyTeams?: Named[] }) {
  // Someone who sets budgets only for the teams they administer chooses among those teams and nothing else.
  const teams = onlyTeams ?? allTeams;
  const { name: currencyName, code, toUsd, toDisplay } = useMoney();
  const editing = target.editing?.budget;
  const start = editing?.scope ?? target.scope;
  const [name, setName] = useState(editing?.name ?? (target.scope?.type === 'project' ? `${names.project.get(target.scope.id) ?? 'Project'}, this month` : ''));
  const [type, setType] = useState<ScopeType>(start?.type ?? (onlyTeams ? 'team' : 'project'));
  const [id, setId] = useState(start && start.type !== 'tenant' ? start.id : '');
  const [person, setPerson] = useState<{ id: string; name: string }>();
  const [amount, setAmount] = useState(editing ? String(Math.round(toDisplay(editing.amountUsd) * 100) / 100) : '100');
  const [period, setPeriod] = useState<Budget['period']>(editing?.period ?? 'month');
  const [warnAt, setWarnAt] = useState(String(editing?.warnAtPercent ?? 80));
  const [action, setAction] = useState<Budget['action']>(editing?.action ?? 'warn');
  const [overrideRole, setOverrideRole] = useState<Budget['overrideRole']>(editing?.overrideRole ?? 'owner');
  const [share, setShare] = useState(editing?.childDefault ? String(editing.childDefault.percent) : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const options = type === 'area' ? areas : type === 'project' ? projects : type === 'team' ? teams : [];
  const scopeId = type === 'person' ? person?.id : type === 'tenant' ? undefined : id || options[0]?.id;
  const sharePercent = share.trim() ? Number(share) : undefined;
  const shareOk = sharePercent === undefined || (Number.isFinite(sharePercent) && sharePercent >= 1 && sharePercent <= 100);
  const ready = name.trim() && Number(amount) > 0 && (type === 'tenant' || scopeId) && shareOk;
  const save = async () => {
    setError(undefined);
    setBusy(true);
    const common = { name: name.trim(), amountUsd: toUsd(Number(amount)), period, warnAtPercent: Number(warnAt) || 80, action, overrideRole };
    try {
      if (editing) {
        await routes.updateBudget(editing.id, { ...common, ...(type === 'area' ? { childDefault: sharePercent ? { percent: sharePercent } : editing.childDefault ? null : undefined } : {}) });
      } else {
        await routes.createBudget({ ...common, scope: type === 'tenant' ? { type } : { type, id: scopeId! }, ...(type === 'area' && sharePercent ? { childDefault: { percent: sharePercent } } : {}) });
        setName('');
      }
      onDone();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  const scopeWords = editing ? (editing.scope.type === 'tenant' ? 'The whole organisation' : editing.scope.type === 'person' ? <SubjectName id={editing.scope.id} /> : `${SCOPE_TEXT[editing.scope.type]}: ${names[editing.scope.type].get(editing.scope.id) ?? editing.scope.id}`) : undefined;
  return (
    <FormSheet
      title={editing ? `Change ${editing.name}` : 'New budget'}
      description="People are told at 50, 80 and 100 per cent of the limit; past it, the action you choose applies until the next period or a lift."
      onClose={onCancel}
      footer={
        <>
          <Button size="sm" variant="tertiary" onPress={onCancel}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" isDisabled={!ready || busy} isPending={busy} onPress={() => void save()}>
            {editing ? 'Save the budget' : 'Set budget'}
          </Button>
        </>
      }
    >
      <FormField label="Name">
        <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Platform team, this month" autoFocus={!editing} />
      </FormField>
      {editing ? (
        <FormField label="On" hint="Where a budget applies is fixed once it is set.">
          <span className="text-sm text-foreground">{scopeWords}</span>
        </FormField>
      ) : (
        <FormField label="On">
          <ChoiceSelect label="On" value={type} options={onlyTeams ? ({ team: SCOPE_TEXT.team } as Record<ScopeType, string>) : SCOPE_TEXT} onChange={(t) => (setType(t), setId(''))} className="w-full" />
        </FormField>
      )}
      {!editing && type === 'person' ? (
        <FormField label="Who" hint={person ? `Chosen: ${person.name}` : undefined}>
          <SubjectPicker kinds={['user']} placeholder="Find the person" onPick={(p) => setPerson({ id: p.id, name: p.name })} />
        </FormField>
      ) : !editing && type !== 'tenant' ? (
        <FormField label="Which">{type === 'project' ? <ProjectScope id={id} onPick={setId} /> : options.length ? <ChoiceSelect label="Which" value={scopeId} options={Object.fromEntries(options.map((o) => [o.id, o.name]))} onChange={setId} className="w-full" /> : <span className="text-sm text-muted">None yet</span>}</FormField>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label={`Amount, ${currencyName}`} hint={code !== 'USD' && Number(amount) > 0 ? `Kept as US$${toUsd(Number(amount)).toFixed(2)} at today's rate and shown back in ${currencyName} at each day's rate.` : undefined}>
          <input className={`${inputClass} tabular-nums`} type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </FormField>
        <FormField label="Every">
          <ChoiceSelect label="Every" value={period} options={EVERY_TEXT} onChange={setPeriod} className="w-full min-w-0" />
        </FormField>
        <FormField label="Warn at, percent">
          <input className={`${inputClass} tabular-nums`} type="number" min="1" max="100" value={warnAt} onChange={(e) => setWarnAt(e.target.value)} />
        </FormField>
        <FormField label="At the limit">
          <ChoiceSelect label="At the limit" value={action} options={ACTION_TEXT} onChange={setAction} className="w-full min-w-0" />
        </FormField>
      </div>
      {type === 'area' && (
        <FormField label="Default share for projects, percent" hint={sharePercent && shareOk ? `Each project in the area without a budget of its own gets ${sharePercent}% of this limit, with the same period and action.` : 'Leave it empty and projects without their own budget have none; set it and each gets that share of this limit.'}>
          <input className={`${inputClass} tabular-nums`} type="number" min="1" max="100" value={share} onChange={(e) => setShare(e.target.value)} placeholder="None" />
          {!shareOk && <span className="text-xs text-danger">A share is a percent from 1 to 100.</span>}
        </FormField>
      )}
      <FormField label="Who may lift it">
        <ChoiceSelect label="Who may lift it" value={overrideRole} options={ROLE_TEXT} onChange={setOverrideRole} className="w-full" />
      </FormField>
      <ErrorNote error={error} />
    </FormSheet>
  );
}

/** One line on a project's overview: where the project stands against its budgets. */
export function ProjectBudgetLine({ projectId, owner }: { projectId: string; owner: boolean }) {
  const { format } = useMoney();
  const b = useLoad(() => routes.projectBudget(projectId), [projectId], 60_000);
  const [reason, setReason] = useState('');
  const [lifting, setLifting] = useState<string>();
  const [error, setError] = useState<string>();
  const d = b.data;
  if (!d || d.budgets.length === 0) return null;
  const held = d.budgets.filter((s) => s.state === 'throttled' || s.state === 'blocked');
  const lift = async (budgetId: string) => {
    setError(undefined);
    try {
      await routes.overrideBudget(projectId, { budgetId, reason: reason.trim(), hours: 24 });
      setLifting(undefined);
      setReason('');
      b.reload();
    } catch (e) {
      const body = (e as { body?: { message?: string } }).body;
      setError(body?.message ?? (e as Error).message);
    }
  };
  return (
    <ListGroup title="Budget" description={`${format(d.spent.usd)} spent since ${timeAgo(d.spent.since)}${d.remaining.usd !== undefined ? `, ${format(d.remaining.usd)} remaining on the project's own budget` : ''}.`}>
      {d.budgets.map((s) => (
        <ListRow
          key={s.budget.id}
          icon={<CreditCard />}
          title={s.inherited ? `Inherited: ${s.inherited.percent}% of ${s.inherited.name}` : s.budget.name}
          description={`${format(s.spent.usd)} of ${format(s.budget.amountUsd)} ${PERIOD_TEXT[s.budget.period]}`}
          status={<StatusMark state={STATE_MARK[s.state]} label={BUDGET_STATE_TEXT[s.state]} />}
          {...(held.includes(s) && owner && lifting !== s.budget.id
            ? {
                control: (
                  <Button size="sm" variant="secondary" onPress={() => setLifting(s.budget.id)}>
                    Lift for a day
                  </Button>
                ),
              }
            : {})}
        >
          {lifting === s.budget.id ? (
            <div className="flex flex-wrap items-center gap-2">
              <input className={`${inputClass} min-w-0 max-w-md flex-1 basis-56`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why the work should go on" aria-label="Reason" />
              <Button size="sm" variant="ghost" onPress={() => (setLifting(undefined), setReason(''))}>
                Cancel
              </Button>
              <Button size="sm" variant="primary" isDisabled={!reason.trim()} onPress={() => void lift(s.budget.id)}>
                Lift
              </Button>
            </div>
          ) : undefined}
        </ListRow>
      ))}
      {error || b.error ? (
        <ListBlock>
          <ErrorNote error={error ?? b.error} />
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}

/** The bar, with the warning line marked. */
function Meter({ s }: { s: BudgetStanding }) {
  const fill = s.state === 'ok' ? 'bg-accent' : s.state === 'warning' ? 'bg-warning' : 'bg-danger';
  const projected = s.forecast ? Math.min(100, s.forecast.percent) : 0;
  return (
    <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-surface-secondary" aria-hidden="true">
      {projected > s.percent && <div className={`absolute inset-y-0 left-0 rounded-full ${fill} opacity-25`} style={{ width: `${projected}%` }} />}
      <div className={`absolute inset-y-0 left-0 rounded-full ${fill}`} style={{ width: `${Math.min(100, s.percent)}%` }} />
      <div className="absolute inset-y-0 w-px bg-foreground/30" style={{ left: `${s.budget.warnAtPercent}%` }} />
    </div>
  );
}

const ordinal = (n: number) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;

/** "On track to reach 100% by the 24th", or where the period ends at this rate. */
function forecastWords(s: BudgetStanding, timeZone: string, format: (n: number) => string): string {
  const f = s.forecast!;
  if (s.percent >= 100) return 'The limit is reached for this period.';
  if (f.exceeds && f.reachesLimitAt) {
    const day = Number(new Intl.DateTimeFormat('en-AU', { timeZone, day: 'numeric' }).format(new Date(f.reachesLimitAt)));
    return `On track to reach 100% by the ${ordinal(day)}, at the last seven days' rate.`;
  }
  return `On track to end the ${s.budget.period} at ${Math.round(f.percent)}% (${format(f.usd)}), at the last seven days' rate.`;
}

const TIME_ZONES: string[] = (() => {
  try {
    return (Intl as unknown as { supportedValuesOf(k: string): string[] }).supportedValuesOf('timeZone');
  } catch {
    return ['UTC', 'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane', 'Australia/Adelaide', 'Australia/Perth', 'Pacific/Auckland'];
  }
})();

/**
 * Administrators: where periods turn over, and what a workbench does when it cannot reach the platform.
 * Each is a choice that applies at once, so each saves as it changes (Layout.tsx rule 5).
 */
function BudgetSettings({ initial, onSaved }: { initial: { timeZone: string; failClosed: boolean }; onSaved: () => void }) {
  const [timeZone, setTimeZone] = useState(initial.timeZone);
  const [failClosed, setFailClosed] = useState(initial.failClosed);
  const [error, setError] = useState<string>();
  const zones = TIME_ZONES.includes('UTC') ? TIME_ZONES : ['UTC', ...TIME_ZONES];
  const save = async (next: { timeZone: string; failClosed: boolean }, undo: () => void) => {
    setError(undefined);
    try {
      await routes.setBudgetSettings({ timeZone: next.timeZone, budgets: { failClosed: next.failClosed } });
      onSaved();
    } catch (e) {
      undo();
      setError((e as { body?: { message?: string } }).body?.message ?? (e as Error).message);
    }
  };
  return (
    <ListGroup title="Settings" description="For every budget in the organisation. Each change applies at once.">
      <ListRow
        title="Time zone"
        description="Days, weeks and months of budgets and chargeback turn over at midnight here."
        control={
          <ChoiceSelect
            label="Time zone"
            value={timeZone}
            options={Object.fromEntries(zones.map((z) => [z, z.replace(/_/g, ' ')]))}
            onChange={(z) => {
              const was = timeZone;
              setTimeZone(z);
              void save({ timeZone: z, failClosed }, () => setTimeZone(was));
            }}
            className="w-64 max-w-full"
          />
        }
      />
      <ListRow
        title="Stop work when the platform cannot be reached"
        description="Off: a workbench keeps its last reading and carries on. On: it starts nothing new until it hears from the platform again."
        control={
          <Toggle
            checked={failClosed}
            label="Stop work when the platform cannot be reached"
            onChange={(on) => {
              setFailClosed(on);
              void save({ timeZone, failClosed: on }, () => setFailClosed(!on));
            }}
          />
        }
      />
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}

/** What a money budget leaves out, when there is any: paid seats' list value and calls with no known price. */
function notCounted(spent: { subscriptionUsd?: number; unpricedCalls?: number }, format: (n: number) => string): string {
  const parts = [spent.subscriptionUsd ? `${format(spent.subscriptionUsd)} on paid seats` : '', spent.unpricedCalls ? `${spent.unpricedCalls.toLocaleString()} unpriced ${spent.unpricedCalls === 1 ? 'call' : 'calls'}` : ''].filter(Boolean);
  return parts.length ? ` · not in the spend: ${parts.join(', ')}` : '';
}
